import type { TokenData } from '@contexts/auth-provider/AuthProvider.types';

import { jwtDecode } from 'jwt-decode';

import type { B2CTokenClaims, B2ETokenClaims } from './extractAuthData.types';

/**
 * Extract user data from JWT token
 *
 * Parses B2C (customer) or B2E (employee) tokens and extracts user information.
 * Roles are NOT extracted here - they come from organizations API.
 *
 * @param idToken - JWT token string from MSAL
 * @returns TokenData with user info (id, email, firstName, lastName, isInternalUser)
 * @throws Error if token is invalid or missing required fields
 *
 * @example
 * const tokenData = extractAuthData('eyJhbGciOiJSUzI1NiIs...');
 */
export function extractAuthData(idToken: string): TokenData {
  try {
    const decoded = jwtDecode<B2ETokenClaims | B2CTokenClaims>(idToken);

    const isB2E = 'oid' in decoded;

    if (isB2E) {
      const b2eToken = decoded;

      // Validate required fields
      if (!b2eToken.preferred_username) {
        throw new Error(
          'B2E token missing required field (preferred_username)'
        );
      }
      if (!b2eToken.name) {
        throw new Error('B2E token missing required field (name)');
      }

      // Parse name into firstName and lastName
      const nameParts = b2eToken.name.trim().split(/\s+/);
      const firstName = nameParts[0];
      const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';

      return {
        id: b2eToken.oid,
        email: b2eToken.preferred_username,
        firstName,
        lastName,
        isInternalUser: true,
      };
    } else {
      const b2cToken = decoded;

      // Validate required fields
      if (!b2cToken.email) {
        throw new Error('B2C token missing required field (email)');
      }
      if (!b2cToken.given_name || !b2cToken.family_name) {
        throw new Error(
          'B2C token missing required fields (given_name or family_name)'
        );
      }

      return {
        id: b2cToken.userId,
        email: b2cToken.email,
        firstName: b2cToken.given_name,
        lastName: b2cToken.family_name,
        isInternalUser: false,
      };
    }
  } catch (error) {
    console.error('Error extracting auth data from token:', error);
    throw new Error('Invalid token format');
  }
}
